import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, limit, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logError, log } from '../utils/logger';
import { useAuth } from './AuthContext';
import pushNotificationService from '../services/pushNotifications';
import { useNotifications } from './NotificationContext';

export interface Medication {
  id: string;
  name: string;
  reminderTime: string;
  isActive: boolean;
  reminderType?: 'daily' | 'post-exposure';
  postExposureHours?: number;
  created: Date;
}

export interface MedicationLog {
  id: string;
  medicationId: string;
  date: Date;
  taken: boolean;
  takenAt?: Date;
}

export interface STIScreening {
  id: string;
  date: Date;
  tests: string[];
  results: Record<string, 'negative' | 'positive' | 'pending'>;
  created: Date;
}

export interface HealthContextType {
  medications: Medication[];
  medicationLogs: MedicationLog[];
  screenings: STIScreening[];
  lastScreeningDate: Date | null;
  loading: boolean;
  reminderSettings: {
    timeBasedMonths: number;
    unprotectedEncountersThreshold: number;
    protectedEncountersThreshold: number;
  };
  hasPendingMedications: () => boolean;
  hasOverdueScreening: () => boolean;
  addMedication: (medication: Omit<Medication, 'id' | 'created'>) => Promise<string | null>;
  updateMedication: (medicationId: string, updates: Partial<Medication>) => Promise<void>;
  deleteMedication: (medicationId: string) => Promise<void>;
  markMedicationTaken: (medicationId: string) => Promise<void>;
  addScreeningRecord: (screening: Omit<STIScreening, 'id' | 'created'>) => Promise<string | null>;
  updateScreeningRecord: (screeningId: string, updates: Partial<STIScreening>) => Promise<void>;
  deleteScreeningRecord: (screeningId: string) => Promise<void>;
  checkIfMedicationDue: (medicationId: string) => boolean;
}

const HealthContext = createContext<HealthContextType | undefined>(undefined);

export const useHealth = () => {
  const context = useContext(HealthContext);
  if (!context) {
    throw new Error('useHealth must be used within HealthProvider');
  }
  return context;
};

interface HealthProviderProps {
  children: ReactNode;
}

export const HealthProvider: React.FC<HealthProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const currentUserId = user?.uid || '';
  const { showInfo } = useNotifications();
  
  const [medications, setMedications] = useState<Medication[]>([]);
  const [medicationLogs, setMedicationLogs] = useState<MedicationLog[]>([]);
  const [screenings, setScreenings] = useState<STIScreening[]>([]);
  const [loading, setLoading] = useState(true);

  // Firebase real-time listeners for medications
  useEffect(() => {
    if (!currentUserId) return;

    const medicationsRef = collection(db, 'users', currentUserId, 'medications');
    const medicationsQuery = query(medicationsRef, orderBy('created', 'desc'), limit(100));

    const unsubscribe = onSnapshot(medicationsQuery, (snapshot) => {
      const userMedications: Medication[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        userMedications.push({
          id: doc.id,
          name: data.name,
          reminderTime: data.reminderTime,
          isActive: data.isActive,
          reminderType: data.reminderType,
          postExposureHours: data.postExposureHours,
          created: data.created?.toDate() || new Date()
        });
      });
      setMedications(userMedications);
      setLoading(false);
    }, (error) => {
      logError('Error fetching medications:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Firebase real-time listeners for medication logs
  useEffect(() => {
    if (!currentUserId) return;

    const logsRef = collection(db, 'users', currentUserId, 'medicationLogs');
    const logsQuery = query(logsRef, orderBy('date', 'desc'), limit(200));

    const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
      const logs: MedicationLog[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          medicationId: data.medicationId,
          date: data.date?.toDate() || new Date(),
          taken: data.taken,
          takenAt: data.takenAt?.toDate()
        });
      });
      setMedicationLogs(logs);
    }, (error) => {
      logError('Error fetching medication logs:', error);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Firebase real-time listeners for screenings
  useEffect(() => {
    if (!currentUserId) return;

    const screeningsRef = collection(db, 'users', currentUserId, 'stiScreenings');
    const screeningsQuery = query(screeningsRef, orderBy('date', 'desc'), limit(50));

    const unsubscribe = onSnapshot(screeningsQuery, (snapshot) => {
      const userScreenings: STIScreening[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        userScreenings.push({
          id: doc.id,
          date: data.date?.toDate() || new Date(),
          tests: data.tests || [],
          results: data.results || {},
          created: data.created?.toDate() || new Date()
        });
      });
      setScreenings(userScreenings);
    }, (error) => {
      logError('Error fetching STI screenings:', error);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  // Reminder settings
  const [reminderSettings] = useState({
    timeBasedMonths: 3,
    unprotectedEncountersThreshold: 10,
    protectedEncountersThreshold: 20
  });

  // Health notifications effect - check for due medications and screenings
  useEffect(() => {
    if (!currentUserId || medications.length === 0) return;

    const checkHealthNotifications = () => {
      // Check for due medications
      medications.forEach(med => {
        if (checkIfMedicationDue(med.id)) {
          // Show in-app notification
          showInfo(`💊 Time to take your ${med.name}`, 'Click to mark as taken', {
            label: 'Mark as Taken',
            onClick: () => markMedicationTaken(med.id)
          });

          // Send push notification
          pushNotificationService.sendMedicationReminderNotification(med.name);
        }
      });

      // Check for overdue screenings
      if (hasOverdueScreening()) {
        showInfo(`🏥 STI Screening Due`, 'It\'s time for your regular STI screening', {
          label: 'View Health',
          onClick: () => window.location.href = '/health'
        });

        // Send push notification
        pushNotificationService.sendScreeningReminderNotification();
      }
    };

    // Check immediately
    checkHealthNotifications();

    // Set up periodic checks (every 30 minutes)
    const healthCheckInterval = setInterval(checkHealthNotifications, 30 * 60 * 1000);

    return () => clearInterval(healthCheckInterval);
  }, [medications, screenings, currentUserId]);

  // Check if any medication is due
  const hasPendingMedications = (): boolean => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinutes;

    // Check daily medications
    const hasDailyDue = medications.some(med => {
      if (!med.isActive || med.reminderType !== 'daily') return false;
      
      // Check if medication has been taken today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const takenToday = medicationLogs.some(log => 
        log.medicationId === med.id && 
        new Date(log.date).toDateString() === today.toDateString() &&
        log.taken
      );
      
      if (takenToday) return false;
      
      // Check if within ±1 hour of reminder time
      const [hours, minutes] = med.reminderTime.split(':').map(Number);
      const reminderTimeInMinutes = hours * 60 + minutes;
      const timeDiff = Math.abs(currentTimeInMinutes - reminderTimeInMinutes);
      
      return timeDiff <= 60; // Within 60 minutes
    });

    // Check post-exposure medications (DoxyPEP)
    // TODO: This would check activity logs for recent unprotected encounters
    const hasPostExposureDue = false; // Placeholder - would check activity logs

    return hasDailyDue || hasPostExposureDue;
  };

  // Check if screening is overdue
  const hasOverdueScreening = (): boolean => {
    if (screenings.length === 0) return true;
    
    const lastScreening = screenings[0];
    const monthsSinceLastScreen = Math.floor(
      (new Date().getTime() - lastScreening.date.getTime()) / (1000 * 60 * 60 * 24 * 30)
    );
    
    return monthsSinceLastScreen >= reminderSettings.timeBasedMonths;
  };

  // Check if specific medication is due
  const checkIfMedicationDue = (medicationId: string): boolean => {
    const med = medications.find(m => m.id === medicationId);
    if (!med || !med.isActive) return false;

    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if already taken today
    const takenToday = medicationLogs.some(log => 
      log.medicationId === medicationId && 
      new Date(log.date).toDateString() === today.toDateString() &&
      log.taken
    );

    if (takenToday) return false;

    if (med.reminderType === 'daily') {
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinutes;
      
      const [hours, minutes] = med.reminderTime.split(':').map(Number);
      const reminderTimeInMinutes = hours * 60 + minutes;
      const timeDiff = Math.abs(currentTimeInMinutes - reminderTimeInMinutes);
      
      return timeDiff <= 60;
    }

    // TODO: Check for post-exposure medications
    return false;
  };

  // CRUD operations for medications
  const addMedication = async (medicationData: Omit<Medication, 'id' | 'created'>): Promise<string | null> => {
    try {
      const docRef = await addDoc(collection(db, 'users', currentUserId, 'medications'), {
        ...medicationData,
        created: serverTimestamp()
      });
      log('Added new medication:', docRef.id);
      return docRef.id;
    } catch (error) {
      logError('Error adding medication:', error);
      return null;
    }
  };

  const updateMedication = async (medicationId: string, updates: Partial<Medication>): Promise<void> => {
    try {
      await updateDoc(doc(db, 'users', currentUserId, 'medications', medicationId), updates);
      log('Updated medication:', medicationId);
    } catch (error) {
      logError('Error updating medication:', error);
    }
  };

  const deleteMedication = async (medicationId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'medications', medicationId));
      log('Deleted medication:', medicationId);
    } catch (error) {
      logError('Error deleting medication:', error);
    }
  };

  // Mark medication as taken
  const markMedicationTaken = async (medicationId: string): Promise<void> => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const existingLog = medicationLogs.find(
        log => log.medicationId === medicationId && 
        new Date(log.date).toDateString() === today.toDateString()
      );

      if (existingLog) {
        await updateDoc(doc(db, 'users', currentUserId, 'medicationLogs', existingLog.id), {
          taken: true,
          takenAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'users', currentUserId, 'medicationLogs'), {
          medicationId,
          date: today,
          taken: true,
          takenAt: serverTimestamp()
        });
      }
      log('Marked medication as taken:', medicationId);
    } catch (error) {
      logError('Error marking medication as taken:', error);
    }
  };

  // CRUD operations for STI screenings
  const addScreeningRecord = async (screeningData: Omit<STIScreening, 'id' | 'created'>): Promise<string | null> => {
    try {
      const docRef = await addDoc(collection(db, 'users', currentUserId, 'stiScreenings'), {
        ...screeningData,
        created: serverTimestamp()
      });
      log('Added new STI screening:', docRef.id);
      return docRef.id;
    } catch (error) {
      logError('Error adding STI screening:', error);
      return null;
    }
  };

  const updateScreeningRecord = async (screeningId: string, updates: Partial<STIScreening>): Promise<void> => {
    try {
      await updateDoc(doc(db, 'users', currentUserId, 'stiScreenings', screeningId), updates);
      log('Updated STI screening:', screeningId);
    } catch (error) {
      logError('Error updating STI screening:', error);
    }
  };

  const deleteScreeningRecord = async (screeningId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'stiScreenings', screeningId));
      log('Deleted STI screening:', screeningId);
    } catch (error) {
      logError('Error deleting STI screening:', error);
    }
  };

  const value: HealthContextType = {
    medications,
    medicationLogs,
    screenings,
    lastScreeningDate: screenings.length > 0 ? screenings[0].date : null,
    loading,
    reminderSettings,
    hasPendingMedications,
    hasOverdueScreening,
    addMedication,
    updateMedication,
    deleteMedication,
    markMedicationTaken,
    addScreeningRecord,
    updateScreeningRecord,
    deleteScreeningRecord,
    checkIfMedicationDue
  };

  return (
    <HealthContext.Provider value={value}>
      {children}
    </HealthContext.Provider>
  );
};

export default HealthContext;